<template>
  <v-app>
    <v-row no-gutters>
      <v-col cols="12" lg="7" class="d-none d-lg-block">
        <auth-signup-image :image="require('@/assets/img/auth/signup/signUpImage1.webp')" />
      </v-col>
      <v-col cols="12" lg="5">
        <authentication-service-provider
          v-slot="{ signUp, signupSocial, loaders, forms, errorMessage }"
          @signup-success="onSignupSuccess"
          @signup-social-success="onSignupSocialSuccess"
        >
          <div class="wrapper">
            <locale-changer class="pb-3" />

            <div v-if="!showVerificationForm" class="form-wrapper">
              <div class="title-wrapper"><!-- Title -->
                <h1>
                  {{ $t('auth.signUp.welcome') }}
                </h1>
                <!-- /Title -->
                <p>{{ $t('auth.signUp.getStarted') }}</p>
              </div>
              <!-- Alert -->
              <v-alert
                v-show="errorMessage"
                border="left"
                color="red"
                type="error"
              >
                {{ errorMessage }}
              </v-alert>
              <!-- /Alert -->

              <!-- Signup with Google -->
              <v-btn
                class="button-google"
                block
                x-large
                elevation="0"
                outlined
                :disabled="loaders.socialSignup || loaders.signUp"
                :loading="loaders.socialSignup"
                color="secondary"
                @click="signupSocial('google')"
              >
                <img src="@/assets/img/logos/google.png" alt="google" class="mr-2">
                <span>{{ $t('auth.signUp.signUpWithGoogle') }}</span>
              </v-btn>
              <!-- Signup with Google -->

              <!-- Or -->
              <p class="line-through">
                {{ $t('auth.global.or') }}
              </p>
              <!-- /Or -->

              <validation-observer v-slot="{ invalid }">
                <v-form @submit.native.prevent="signUp()">
                  <validation-provider
                    v-slot="{ errors }"
                    name="email"
                    rules="required|email"
                  >
                    <v-text-field
                      v-model="forms.signUp.email"
                      :label="$t('auth.global.email')"
                      outlined
                      required
                      :disabled="loaders.signUp || loaders.socialSignup"
                      background-color="white"
                      :error-messages="errors"
                      :placeholder="$t('auth.global.enterEmail')"
                    />
                  </validation-provider>
                  <validation-provider
                    v-slot="{ errors }"
                    name="password"
                    rules="required|min:6"
                  >
                    <v-text-field
                      v-model="forms.signUp.password"
                      :label="$t('auth.global.password')"
                      outlined
                      required
                      :disabled="loaders.signUp || loaders.socialSignup"
                      background-color="white"
                      :type="showPassword ? 'text' : 'password'"
                      :placeholder="$t('auth.global.enterPassword')"
                      :error-messages="errors"
                    >
                      <template #append>
                        <span @click="showPassword = !showPassword">
                          <v-icon>
                            {{ showPassword ? icons.mdiEye : icons.mdiEyeOff }}
                          </v-icon>
                        </span>
                      </template>
                    </v-text-field>
                  </validation-provider>

                  <v-btn
                    block
                    x-large
                    color="primary"
                    elevation="0"
                    type="submit"
                    :disabled="loaders.signUp || loaders.socialSignup || invalid"
                    :loading="loaders.signUp"
                    @click="signUp()"
                  >
                    <span class="font-outfit-semi-bold button-continue">{{ $t('continue') }}</span>
                  </v-btn>
                </v-form>
              </validation-observer>

              <!-- Terms & Conditions -->
              <p class="disclaimer-text">{{ $t('auth.global.byContinuing') }}
                <a href="https://ideabuddy.com/terms-of-service/" target="_blank">{{ $t('auth.global.termsAndConditions') }}</a> {{ $t('auth.global.and') }}
                <a href="https://ideabuddy.com/privacy-policy/" target="_blank">{{ $t('auth.global.privacyPolicy') }}</a>.
              </p>
              <!-- /Terms & Conditions -->

              <!-- Go To Login -->
              <p class="already-have">
                <span>{{ $t('auth.signUpPayment.alreadyHave') }}</span>
                &nbsp;
                <router-link :to="{name: 'login'}">
                  {{ $t('auth.signUp.logIn') }}
                </router-link>
              </p>
              <!-- /Go To Login -->
            </div>

            <!-- Verification form -->
            <verification-email-form v-if="showVerificationForm" />
            <!-- /Verification form -->
          </div>
        </authentication-service-provider>
      </v-col>
    </v-row>
  </v-app>
</template>
<script>
import { mdiEye, mdiEyeOff } from '@mdi/js'
import AuthSignupImage from '@/components/_v2/Common/AuthSignupImage.vue'
import AuthenticationServiceProvider from '@/components/ServiceProviders/AuthenticationServiceProvider.vue'
import LocaleChanger from '@/components/LocaleChanger.vue'
import VerificationEmailForm from '@/views/Signup/Components/VerificationEmailForm'
import { mapGetters } from 'vuex'

export default {
  name: 'Signup',

  components: { AuthenticationServiceProvider, LocaleChanger, AuthSignupImage, VerificationEmailForm },

  data () {
    return {
      showVerificationForm: false,
      showPassword: false,
      icons: { mdiEye, mdiEyeOff }
    }
  },

  computed: {
    ...mapGetters('user', ['isVerified'])
  },

  created () {
    if (this.$router.currentRoute.name === 'signup') {
      localStorage.removeItem('persisted_state')
      this.$store.dispatch('resetState')
    } else {
      this.showVerificationForm = true
    }
  },

  methods: {
    async onSignupSuccess () {
      if (this.isVerified) {
        await this.$router.push({ name: 'select-idea', query: this.$router.currentRoute.query })
      } else {
        this.showVerificationForm = true
      }
    },

    onSignupSocialSuccess () {
      this.$router.push({ name: 'select-idea', query: this.$router.currentRoute.query })
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  padding: 5%;
  background: #F5F6FA;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  text-align: center;
  color: #292f4d;

  @include media-breakpoint-up($sm) {
    padding: 10%;
  }

  .form-wrapper {
    width: 100%;
    max-width: 550px;

    .title-wrapper {
      margin-bottom: 40px;

    h1 {
      max-width: 450px;
      margin: 20px auto 0px;
      font-size: 26px;

      @include media-breakpoint-up($sm) {
        font-size: 32px;
      }
     }
      p {
        font-size: 16px;
        margin-top: 8px;

        @include media-breakpoint-up($sm) {
          font-size: 18px;
        }
      }
    }

    .line-through {
      overflow: hidden;
      text-align: center;

      &:before,
      &:after {
        background-color: rgba(41, 47, 77, 0.09);
        content: "";
        display: inline-block;
        height: 1px;
        position: relative;
        vertical-align: middle;
        width: 50%;
      }

      &:before {
        right: 1rem;
        margin-left: -50%
      }

      &:after {
        left: 1rem;
        margin-right: -50%;
      }
    }

    span {
      text-transform: none;
    }

    img {
      width: 19px;
    }

    .button-continue {
      text-transform: uppercase;
    }

    .button-google {
      background: white;

      span {
        color: #292f4d;
      }
    }

    .already-have {
      margin-top: 30px;
    }
  }

  .disclaimer-text {
    font-size: 14px;
  }
}
</style>
